import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, X, Zap, CheckCircle, ArrowRight, Trophy, ListTodo, Check } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Task } from '@/types/task';
import { AuditSubmitData } from '@/types/audit';
import { cn } from '@/lib/utils';

interface PostSessionAuditDialogProps {
  isOpen: boolean;
  task: Task | null;
  elapsedMinutes: number;
  /** (auditData, completeTask). completeTask=true marks the session task done; false = save for later. */
  onSubmit: (data: AuditSubmitData, completeTask: boolean) => void;
}

export function PostSessionAuditDialog({
  isOpen,
  task,
  elapsedMinutes,
  onSubmit,
}: PostSessionAuditDialogProps) {
  const isMobile = useIsMobile();
  const [notes, setNotes] = useState('');
  const [accomplishments, setAccomplishments] = useState<string[]>([]);
  const [newAccomplishment, setNewAccomplishment] = useState('');
  const [sideQuests, setSideQuests] = useState<string[]>([]);
  const [newQuest, setNewQuest] = useState('');
  const [todoItems, setTodoItems] = useState<string[]>([]);
  const [newTodo, setNewTodo] = useState('');

  // Reset state when dialog opens
  useEffect(() => {
    if (isOpen) {
      setNotes('');
      setAccomplishments([]);
      setNewAccomplishment('');
      setSideQuests([]);
      setNewQuest('');
      setTodoItems([]);
      setNewTodo('');
    }
  }, [isOpen]);

  // Play chime when dialog opens
  useEffect(() => {
    if (isOpen) {
      playChime();
    }
  }, [isOpen]);

  const playChime = () => {
    try {
      const AudioContextCtor = window.AudioContext ?? (window as Window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      const audioContext = AudioContextCtor ? new AudioContextCtor() : null;
      if (!audioContext) return;
      
      const playTone = (frequency: number, startTime: number, duration: number, volume: number) => {
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();
        
        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);
        
        oscillator.frequency.value = frequency;
        oscillator.type = 'sine';
        
        gainNode.gain.setValueAtTime(0, startTime);
        gainNode.gain.linearRampToValueAtTime(volume, startTime + 0.05);
        gainNode.gain.exponentialRampToValueAtTime(0.01, startTime + duration);
        
        oscillator.start(startTime);
        oscillator.stop(startTime + duration);
      };

      const now = audioContext.currentTime;
      playTone(523.25, now, 0.8, 0.15);
      playTone(659.25, now + 0.15, 0.8, 0.12);
      playTone(783.99, now + 0.3, 1.0, 0.1);
    } catch {
      // Audio not available (e.g. autoplay policy)
    }
  };

  if (!isOpen) return null;

  const handleAddAccomplishment = () => {
    if (newAccomplishment.trim()) {
      setAccomplishments([...accomplishments, newAccomplishment.trim()]);
      setNewAccomplishment('');
    }
  };

  const handleRemoveAccomplishment = (index: number) => {
    setAccomplishments(accomplishments.filter((_, i) => i !== index));
  };

  const handleAddQuest = () => {
    if (newQuest.trim()) {
      setSideQuests([...sideQuests, newQuest.trim()]);
      setNewQuest('');
    }
  };

  const handleRemoveQuest = (index: number) => {
    setSideQuests(sideQuests.filter((_, i) => i !== index));
  };

  const handleAddTodo = () => {
    if (newTodo.trim()) {
      setTodoItems([...todoItems, newTodo.trim()]);
      setNewTodo('');
    }
  };

  const handleRemoveTodo = (index: number) => {
    setTodoItems(todoItems.filter((_, i) => i !== index));
  };

  const handleKeyDown = (
    e: React.KeyboardEvent, 
    addFunction: () => void
  ) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addFunction();
    }
  };

  const auditData = (): AuditSubmitData => ({
    accomplishments,
    sideQuests,
    todoItems,
    notes,
  });

  const handleSaveForLater = () => {
    onSubmit(auditData(), false);
  };

  const handleFinishTask = () => {
    onSubmit(auditData(), true);
  };

  const formatDuration = (minutes: number) => {
    if (minutes >= 60) {
      const hours = Math.floor(minutes / 60);
      const mins = minutes % 60;
      return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
    }
    return `${minutes}m`;
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-background/95 backdrop-blur-sm overflow-y-auto py-8 pt-[max(2rem,env(safe-area-inset-top,0px))] pb-[max(2rem,env(safe-area-inset-bottom,0px))]"
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0, y: 20 }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
          className="relative z-10 flex flex-col items-center gap-6 max-w-md mx-auto px-6 w-full"
        >
          {/* Header */}
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.1, type: "spring", damping: 15 }}
            className="p-3 rounded-full bg-success/10"
          >
            <Trophy className="h-10 w-10 text-success" />
          </motion.div>

          <div className="space-y-2 text-center">
            <motion.h2
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-2xl font-bold"
            >
              Session Complete!
            </motion.h2>
            {task && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
                className="space-y-1"
              >
                <p className="text-muted-foreground">
                  Task: <span className="font-medium text-foreground">{task.content}</span>
                </p>
                <p className="text-sm text-muted-foreground">
                  Duration: <span className="font-medium text-foreground">{formatDuration(elapsedMinutes)}</span>
                </p>
              </motion.div>
            )}
          </div>

          {/* Notes section */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="w-full space-y-3"
          >
            <div className="flex items-center gap-2 text-base font-medium text-muted-foreground">
              <span>Quick notes (optional)</span>
            </div>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any notes about this session..."
              className="min-h-[80px] resize-none text-base rounded-xl"
            />
          </motion.div>

          {/* Accomplishments section */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35 }}
            className="w-full space-y-3"
          >
            <div className="flex items-center gap-2 text-base font-medium">
              <CheckCircle className="h-5 w-5 text-success" />
              <span>What did you accomplish?</span>
            </div>
            
            <p className="text-sm text-muted-foreground">
              Add completed tasks to log in your history.
            </p>

            <div className="flex gap-3">
              <Input
                value={newAccomplishment}
                onChange={(e) => setNewAccomplishment(e.target.value)}
                onKeyDown={(e) => handleKeyDown(e, handleAddAccomplishment)}
                placeholder="Finished the report..."
                className="flex-1 h-12 text-base rounded-xl"
                autoFocus={!isMobile}
              />
              <Button
                type="button"
                variant="outline"
                className="h-12 w-12 rounded-xl"
                onClick={handleAddAccomplishment}
                disabled={!newAccomplishment.trim()}
              >
                <Plus className="h-5 w-5" />
              </Button>
            </div>

            {accomplishments.length > 0 && (
              <div className="space-y-2">
                {accomplishments.map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    className={cn(
                      "flex items-center gap-3 p-3 rounded-xl",
                      "bg-success/10 border border-success/20"
                    )}
                  >
                    <Check className="h-5 w-5 text-success shrink-0" />
                    <span className="flex-1 text-base">{item}</span>
                    <button
                      onClick={() => handleRemoveAccomplishment(index)}
                      className="p-2 rounded-lg hover:bg-success/20 text-muted-foreground hover:text-foreground transition-colors"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </motion.div>
                ))}
              </div>
            )}
          </motion.div>

          {/* Side quests section */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="w-full space-y-3"
          >
            <div className="flex items-center gap-2 text-base font-medium">
              <Zap className="h-5 w-5 text-amber-500" />
              <span>Any side quests?</span>
            </div>
            
            <p className="text-sm text-muted-foreground">
              Unplanned work done during this session.
            </p>

            <div className="flex gap-3">
              <Input
                value={newQuest}
                onChange={(e) => setNewQuest(e.target.value)}
                onKeyDown={(e) => handleKeyDown(e, handleAddQuest)}
                placeholder="Answered emails..."
                className="flex-1 h-12 text-base rounded-xl"
              />
              <Button
                type="button"
                variant="outline"
                className="h-12 w-12 rounded-xl"
                onClick={handleAddQuest}
                disabled={!newQuest.trim()}
              >
                <Plus className="h-5 w-5" />
              </Button>
            </div>

            {sideQuests.length > 0 && (
              <div className="space-y-2">
                {sideQuests.map((quest, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    className={cn(
                      "flex items-center gap-3 p-3 rounded-xl",
                      "bg-amber-500/10 border border-amber-500/20"
                    )}
                  >
                    <Zap className="h-5 w-5 text-amber-500 shrink-0" />
                    <span className="flex-1 text-base">{quest}</span>
                    <button
                      onClick={() => handleRemoveQuest(index)}
                      className="p-2 rounded-lg hover:bg-amber-500/20 text-muted-foreground hover:text-foreground transition-colors"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </motion.div>
                ))}
              </div>
            )}
          </motion.div>

          {/* Still need to do section */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.45 }}
            className="w-full space-y-3"
          >
            <div className="flex items-center gap-2 text-base font-medium">
              <ListTodo className="h-5 w-5 text-primary" />
              <span>Still need to do?</span>
            </div>
            
            <p className="text-sm text-muted-foreground">
              Add tasks for later that came up during this session.
            </p>

            <div className="flex gap-3">
              <Input
                value={newTodo}
                onChange={(e) => setNewTodo(e.target.value)}
                onKeyDown={(e) => handleKeyDown(e, handleAddTodo)}
                placeholder="Review the draft..."
                className="flex-1 h-12 text-base rounded-xl"
              />
              <Button
                type="button"
                variant="outline"
                className="h-12 w-12 rounded-xl"
                onClick={handleAddTodo}
                disabled={!newTodo.trim()}
              >
                <Plus className="h-5 w-5" />
              </Button>
            </div>

            {todoItems.length > 0 && (
              <div className="space-y-2">
                {todoItems.map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    className={cn(
                      "flex items-center gap-3 p-3 rounded-xl",
                      "bg-primary/10 border border-primary/20"
                    )}
                  >
                    <ListTodo className="h-5 w-5 text-primary shrink-0" />
                    <span className="flex-1 text-base">{item}</span>
                    <button
                      onClick={() => handleRemoveTodo(index)}
                      className="p-2 rounded-lg hover:bg-primary/20 text-muted-foreground hover:text-foreground transition-colors"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </motion.div>
                ))}
              </div>
            )}
          </motion.div>

          {/* Actions */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="flex flex-col sm:flex-row gap-4 w-full pt-4"
          >
            <Button
              variant="outline"
              onClick={handleSaveForLater}
              className="flex-1 gap-2 h-14 text-base font-semibold rounded-xl border bg-muted/50 border-border/50"
            >
              <ArrowRight className="h-5 w-5 shrink-0" />
              Save task for later
            </Button>
            <Button
              variant="default"
              onClick={handleFinishTask}
              className="flex-1 gap-2 h-14 text-base font-semibold rounded-xl shadow-lg shadow-primary/20"
            >
              <CheckCircle className="h-5 w-5 shrink-0" />
              Finish task
            </Button>
          </motion.div>

          {/* Info text */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="text-sm text-muted-foreground/70 text-center"
          >
            💡 Items will be added to your task list for today.
          </motion.p>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
